
import React from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Task } from '@/context/TaskContext';

interface UnprioritizedTaskListProps {
  tasks: Task[];
  showTaskList: boolean;
  setShowTaskList: (open: boolean) => void;
  isMobile: boolean;
}

const UnprioritizedTaskList: React.FC<UnprioritizedTaskListProps> = ({
  tasks, showTaskList, setShowTaskList, isMobile
}) => {
  return (
    <Droppable droppableId="unprioritized-list">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          className={cn(
            // Card-like styling to match prioritised cards
            "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm px-4 py-3",
            isMobile ? "w-full" : "w-1/3",
            "flex flex-col",
            snapshot.isDraggingOver ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600 rounded-xl" : ""
          )}
          {...provided.droppableProps}
        >
          {isMobile && (
            <button
              onClick={() => setShowTaskList(!showTaskList)}
              className="flex items-center justify-between w-full p-3 mb-2 bg-gray-50 dark:bg-gray-700 rounded-lg border"
            >
              <span className="font-medium text-gray-900 dark:text-white">
                Tasks ({tasks.length})
              </span>
              {showTaskList ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
          <div className={cn(
            isMobile && !showTaskList && "hidden",
            "flex-1 overflow-y-auto min-h-[100px] pt-1"
          )}>
            {tasks.map((task, idx) => (
              <Draggable draggableId={task.id} index={idx} key={task.id}>
                {(dragProvided, dragSnapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                    className={cn(
                      "p-2 mb-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 shadow-sm hover:shadow-md cursor-move",
                      dragSnapshot.isDragging && "opacity-60 ring-2 ring-blue-500")
                    }
                  >
                    <div className="text-xs font-medium text-gray-900 dark:text-white">{task.title}</div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {tasks.length === 0 && (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                No tasks to prioritize
              </div>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );
};

export default UnprioritizedTaskList;

